import type { TProduct } from "@lexicanon/shared"
import { createContext, useContext } from "react"
import { apiUrl } from "./runtime-origin"

/**
 * Detect product — runs synchronously before any API call.
 *
 * The desktop (Tauri) build bakes `VITE_PRODUCT` at build time because the
 * webview has no brand hostname to read; the web build falls back to hostname
 * detection (lexicanon.com vs voxcanon.com).
 */
export function detectProduct(): TProduct {
  const forced = import.meta.env.VITE_PRODUCT
  if (forced === "lexicanon" || forced === "voxcanon") return forced
  return window.location.hostname.includes("voxcanon") ? "voxcanon" : "lexicanon"
}

export interface TenantBranding {
  name: string
  slug: string | null
  organizationId: string | null
  logo: string | null
  brandColor: string | null
  /** 10-shade Mantine palette derived server-side from brandColor (Phase 2 white-label).
   *  Null when the org hasn't set a brand colour, or when the saved value was malformed. */
  brandPalette: string[] | null
  loginBackground: string | null
  customCss: string | null
  ssoEnabled: boolean
  product?: TProduct
  /** Company-wide default Lexicanon theme key, or null for the global default.
   *  Applied pre-auth (themes the login page) for members with no override. */
  defaultTheme: string | null
}

export interface TenantState {
  isAdmin: boolean // root domain, no tenant = admin portal
  product: TProduct
  branding: TenantBranding | null
  /** The MCP resource URL for this brand (`https://api.<base domain>/mcp`), from the server —
   *  a tenant on a custom domain cannot derive it from its own hostname. */
  mcpUrl: string | null
  loading: boolean
}

export const TenantContext = createContext<TenantState>({
  isAdmin: true,
  product: "lexicanon",
  branding: null,
  mcpUrl: null,
  loading: true,
})

export interface ITenantInfo {
  branding: TenantBranding | null
  mcpUrl: string | null
}

export function useTenant(): TenantState {
  return useContext(TenantContext)
}

export function useProduct(): TProduct {
  return useContext(TenantContext).product
}

export async function fetchTenantBranding(): Promise<ITenantInfo> {
  try {
    const res = await fetch(apiUrl("/api/tenant/branding"))
    if (!res.ok) return { branding: null, mcpUrl: null }
    const data: unknown = await res.json()
    if (typeof data !== "object" || data === null) return { branding: null, mcpUrl: null }
    const tenant = "tenant" in data ? data.tenant : null
    const mcpUrl = "mcpUrl" in data && typeof data.mcpUrl === "string" ? data.mcpUrl : null
    return { branding: isTenantBranding(tenant) ? tenant : null, mcpUrl }
  } catch {
    return { branding: null, mcpUrl: null }
  }
}

function isTenantBranding(value: unknown): value is TenantBranding {
  return typeof value === "object" && value !== null && "name" in value && "slug" in value
}

/** Validate that a URL is a safe absolute https/http URL for CSS embedding */
function isSafeUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return (url.protocol === "https:" || url.protocol === "http:") && !value.includes("'") && !value.includes(")")
  } catch {
    return false
  }
}

/**
 * Validate that a value looks like a 10-shade Mantine palette of hex strings.
 * Defensive against malformed metadata that survived a partial save.
 */
function isValidPalette(value: unknown): value is string[] {
  if (!Array.isArray(value)) return false
  if (value.length !== 10) return false
  return value.every(
    (shade) => typeof shade === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(shade.trim()),
  )
}

export function applyBranding(branding: TenantBranding): void {
  const root = document.documentElement

  // Apply the 10-shade palette per company. Mantine's `cssVariablesResolver`
  // returns `var(--lex-brand-N)` for the brand slot mapping, but Mantine
  // resolves that to a literal hex at theme construction and emits the hex
  // into the document <style> tag — so an override on the Layer 1.5 alias
  // (`--lex-brand-N`) alone does NOT propagate to `--mantine-color-brand-N`
  // at runtime. We override BOTH the alias (for any consumer that reads the
  // Layer 1.5 token directly) AND the resolved Mantine slots (which is what
  // every `color="brand"` consumer reads in practice).
  //
  // Voxcanon tenants intentionally skip the override — the gaming brand has
  // no white-label customers, and Voxcanon's resolver re-binds the brand
  // palette to --vox-arcane*, so layering a tenant hex on top would clash
  // with the parchment / cosmic theming.
  const isVoxcanon = branding.product === "voxcanon"
  if (!isVoxcanon && isValidPalette(branding.brandPalette)) {
    branding.brandPalette.forEach((hex, i) => {
      root.style.setProperty(`--lex-brand-${i}`, hex)
      root.style.setProperty(`--mantine-color-brand-${i}`, hex)
    })
    // Mantine derives a family of filled / light / outline slot tokens from
    // the brand palette and emits resolved hexes for them at theme
    // construction. color="brand" consumers read from these slots (NOT from
    // the -0..-9 ramp directly), so we override each. See BrandPreview for
    // the matching index map.
    const p = branding.brandPalette
    if (p[7]) {
      root.style.setProperty("--mantine-color-brand-filled", p[7])
      root.style.setProperty("--mantine-primary-color-filled", p[7])
    }
    if (p[8]) {
      root.style.setProperty("--mantine-color-brand-filled-hover", p[8])
      root.style.setProperty("--mantine-primary-color-filled-hover", p[8])
    }
    if (p[0]) {
      root.style.setProperty("--mantine-color-brand-light", p[0])
      root.style.setProperty("--mantine-color-brand-outline-hover", p[0])
    }
    if (p[1]) root.style.setProperty("--mantine-color-brand-light-hover", p[1])
    if (p[4]) {
      root.style.setProperty("--mantine-color-brand-light-color", p[4])
      root.style.setProperty("--mantine-color-brand-outline", p[4])
    }
  }

  if (branding.loginBackground && isSafeUrl(branding.loginBackground)) {
    // URL validated: must be absolute https/http with no CSS-breaking characters
    root.style.setProperty("--login-bg", `url('${branding.loginBackground}')`)
  }

  // customCss is no longer applied — Phase 2 ships a real white-label feature
  // so the textarea is dropped from the admin UI. Existing rows keep their
  // value in the database but it's a no-op at runtime. Re-introduce behind a
  // feature flag if a customer asks.
}
